import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDeviceCode } from '@pos/core';
import { patchRow, type DeviceRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Badge, Button, Card, Input, PageHeader, errorMessage } from '../../ui/index.tsx';

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });

export function DevicesPage() {
  const { business, device: thisDevice } = useSession();
  const devices = useLiveQuery(
    () => db.devices.where('business_id').equals(business.id).sortBy('code'),
    [business.id],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Dispositivos"
        description="Celulares, tablets y PCs que pueden usar el sistema. Los empleados solo entran desde estos."
      />
      <Alert tone="info">
        Para sumar un dispositivo, abrí la app en él e ingresá con tu cuenta de dueño. Queda habilitado al instante.
      </Alert>
      <Card flush className="divide-y divide-slate-100">
        {devices?.map((d) => <DeviceItem key={d.id} device={d} isThis={d.id === thisDevice.id} />)}
      </Card>
    </div>
  );
}

function DeviceItem({ device, isThis }: { device: DeviceRow; isThis: boolean }) {
  const { writer } = useSession();
  const [name, setName] = useState(device.name);
  const [renaming, setRenaming] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch = async (changes: Record<string, unknown>) => {
    try {
      await patchRow(db, 'devices', device.id, changes, writer);
      requestSync(0);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div className="space-y-2 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-sm text-slate-700">{formatDeviceCode(device.code)}</span>
        <div className="min-w-0 flex-1">
          {renaming ? (
            <div className="flex gap-2">
              <Input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
              <Button
                onClick={async () => {
                  if (name.trim() && name.trim() !== device.name) await patch({ name: name.trim() });
                  setRenaming(false);
                }}
              >
                OK
              </Button>
            </div>
          ) : (
            <>
              <p className="truncate font-medium">{device.name}</p>
              <p className="text-sm text-slate-500">
                {device.last_seen_at ? `Última conexión: ${dateFormat.format(new Date(device.last_seen_at))}` : 'Sin datos de conexión'}
              </p>
            </>
          )}
        </div>
        {isThis && <Badge tone="brand">Este dispositivo</Badge>}
        {device.revoked_at && <Badge tone="red">Deshabilitado</Badge>}
        {!renaming && !device.revoked_at && (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setRenaming(true)}>
              Renombrar
            </Button>
            {!isThis && (
              <Button variant="dangerGhost" onClick={() => setConfirmRevoke(true)}>
                Deshabilitar
              </Button>
            )}
          </div>
        )}
      </div>
      {confirmRevoke && (
        <Alert tone="warning" title={`¿Deshabilitar "${device.name}"?`}>
          <p>Nadie va a poder usar el sistema desde ese dispositivo. Para volver a usarlo hay que habilitarlo de nuevo.</p>
          <div className="mt-3 flex gap-2">
            <Button variant="secondary" onClick={() => setConfirmRevoke(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                await patch({ revoked_at: new Date().toISOString() });
                setConfirmRevoke(false);
              }}
            >
              Deshabilitar
            </Button>
          </div>
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}
    </div>
  );
}
